<template>
  <div class="wt2-confirm-duplicate wt2-modal-inner">
    <div class="-title wt2-text-l2">{{ title }}</div>
    <small>{{ message }}</small>
    <div class="-name">
      <span class="wt2-color-black">{{
        'makeCopyDialog.storyTitle' | culture
      }}</span>
      <wt-text-field
        v-model="newStoryTitle"
        placeholder="Insert headline..."
        mandatory="true"
        :errorMessage="errors.storyTitle ? 'Please enter video headline' : ''"
        @keyup.native="errors.storyTitle = false"
        @keyup.enter.native="duplicate"
      />
    </div>
    <div
      v-if="showOrganizationList && hasMoreThanOneOrganization"
      class="-organization"
    >
      <span class="wt2-color-black">{{
        'makeCopyDialog.organization' | culture
      }}</span>
      <wt-organizations-dropdown
        type="underline"
        :current-organization-id.sync="currentOrganizationId"
      />
    </div>
    <div v-show="showBrandsList" class="-programmer">
      <span class="wt2-color-black">{{
        'makeCopyDialog.productionLine' | culture
      }}</span>
      <!--        :filter-by-aspect-ratio="story.aspectRatio"-->
      <wt-brands-dropdown
        :organization-id="currentOrganizationId"
        :current-brand.sync="currentBrand"
        :show-single="hasMoreThanOneOrganization"
      />
    </div>
    <div
      v-if="this.currentBrand && !brandHasTheme4aspectRatio"
      class="wt2-color-error"
      v-text="noThemes4aspectRatio"
    />
    <div class="-actions" v-tab-guard>
      <wt-button
        class="-min-width-unset"
        tabindex="0"
        type="transparent"
        :text="'generic.CANCEL' | culture"
        @click="closeModal"
      />
      <wt-button tabindex="-1" :text="buttonText" @click="duplicate" />
    </div>
  </div>
</template>

<script>
import app from 'app';
import { mapState } from 'vuex';
import { DuplicateController } from 'c/DuplicateController';
import WtBrandsDropdown from '#/components/dropdowns/wtBrandsDropdown.vue';
import { AspectRatiosController } from 'c/AspectRatiosController';

export default {
  components: { WtBrandsDropdown },
  store() {
    return app.rootStore;
  },

  props: {
    closeModal: { type: Function, required: true },
    showOrganizationList: { type: Boolean, default: true },
    title: { type: String, default: app.culture.makeCopyDialog.title },
    buttonText: {
      type: String,
      default: app.culture.makeCopyDialog.buttonText,
    },
    story: { type: Object, required: true },
    message: { type: String, required: true },
    onCopySuccess: { type: Function },
    onCopyError: { type: Function },
    storyStartPoint: { type: String, required: false },
    source: { type: String, required: false },
    keepAsStoryboard: { type: Boolean, default: null },
    useStoryOriginalBrandId: { type: Boolean, default: true },
    copyTitle: { type: Boolean, default: true },
    requireTitle: { type: Boolean, default: false },
    resetCategories: { type: Boolean, default: false },
  },

  data() {
    return {
      originalOrganizationId: this.$store.state.user.selectedOrganizationId,
      currentOrganizationId: this.$store.state.user.selectedOrganizationId,
      currentBrand: null,
      newStoryTitle: '',
      errors: {
        storyTitle: false,
      },
    };
  },

  computed: {
    ...mapState('user', ['organizations']),

    currentOrganization() {
      let org = this.organizations.find(
        (org) => org.organizationId === this.currentOrganizationId
      );
      if (!org) {
        org = this.organizations[0];
      }
      return org;
    },

    currentOrganizationBrands() {
      return this.currentOrganization.brands.filter((brand) =>
        brand.themes.find(
          (theme) => theme.aspectRatio === this.story.aspectRatio
        )
      );
    },

    showBrandsList() {
      if (this.hasMoreThanOneOrganization) {
        return true;
      }
      return this.currentOrganizationBrands.length > 1;
    },

    isStoryboard() {
      return _.isBoolean(this.keepAsStoryboard)
        ? this.keepAsStoryboard
        : !!this.story.template;
    },

    hasMoreThanOneOrganization() {
      return this.organizations.length > 1;
    },

    brandHasTheme4aspectRatio() {
      return !!this.currentBrand.themes.find(
        (theme) => theme.aspectRatio === this.story.aspectRatio
      );
    },

    noThemes4aspectRatio() {
      const fallbackTheme = AspectRatiosController.getPrioritizedAspectRatio(
        this.currentBrand.themes
      );
      const fallbackAspectRatio =
        app.ASPECT_RATIOS[fallbackTheme.aspectRatio].friendlyVal;
      const curAspectRatio =
        app.ASPECT_RATIOS[this.story.aspectRatio].friendlyVal;

      return `Aspect Ratio ${curAspectRatio} is not supported in brand "${this.currentBrand.name}", hence we'll fall back to ${fallbackAspectRatio}`;
    },
  },

  created() {
    const originalBrandId =
      this.useStoryOriginalBrandId && this.story ? this.story.brandId : null;
    const originalProgrammerId =
      this.useStoryOriginalBrandId && this.story
        ? this.story.programmerId
        : null;
    let found = this.currentOrganizationBrands.find(
      (brand) =>
        brand.brandId === originalBrandId &&
        brand.programmerId === originalProgrammerId
    );
    if (!found && app.programmers.isSingle()) {
      found = this.currentOrganizationBrands.find(
        (brand) => brand.brandId === originalBrandId
      );
    }

    if (found) {
      this.currentBrand = found;
    } else {
      this.currentBrand =
        this.currentOrganizationBrands.length > 0
          ? this.currentOrganizationBrands[0]
          : null;
    }

    const currentTitle = this.story.title;

    if (this.copyTitle && !_.isEmpty(currentTitle)) {
      const re = /^(.*)\((\d+)\)$/;
      const arr = re.exec(currentTitle);
      let runningNumber;
      if (arr && arr.length) {
        runningNumber = +arr[2];
        if (_.isNumber(runningNumber) && !_.isNaN(runningNumber)) {
          runningNumber++;
          this.newStoryTitle = `${arr[1]}(${runningNumber})`;
        }
      } else {
        this.newStoryTitle = `${currentTitle} (1)`;
      }
    }
  },

  methods: {
    canDuplicate() {
      let rv = true;

      if (this.requireTitle && this.newStoryTitle.length === 0) {
        rv = false;
        app.Vue.set(this.errors, 'storyTitle', true);
      }

      if (!this.currentBrand || !this.currentOrganizationId) {
        app.doctorController.report(
          'wtMakeCopy::duplicate: arguments not suffice'
        );
        rv = false;
      }

      return rv;
    },

    duplicate() {
      if (!this.canDuplicate()) {
        return;
      }

      let brandTheme = this.currentBrand.themes.find(
        (theme) => theme.aspectRatio === this.story.aspectRatio
      );
      if (!brandTheme) {
        brandTheme = AspectRatiosController.getPrioritizedAspectRatio(
          this.currentBrand.themes
        );
      }

      DuplicateController.duplicate(
        {
          orgId: this.currentOrganizationId,
          programmerId: this.currentBrand.programmerId,
          brandId: this.currentBrand.brandId,
          themeId: brandTheme.themeId,
          story: this.story,
          title: this.newStoryTitle,
          storyStartPoint: this.storyStartPoint,
          resetCategories: this.resetCategories,
        },
        {
          source: this.source,
          template: this.story.template,
          keepAsStoryboard: this.keepAsStoryboard,
          modalCaption: app.culture.confirm.coConflict.caption.copy,
        }
      )
        .then((res) => {
          this.closeModal();

          const successData = {
            ...res,
            changeOrg:
              this.currentOrganizationId === this.originalOrganizationId
                ? null
                : this.currentOrganizationId,
          };

          if (successData.changeOrg) {
            app.notificationController.dispatchEvent({
              message: 'DONE',
              data: {
                event: 'STORY_MESSAGE',
                title: 'Video is duplicated',
                description:
                  'Find it on your drafts. Change the view to the relevant workspace.',
                timeout: app.TIME.second(10),
              },
            });
          }

          this.onCopySuccess && this.onCopySuccess(successData);
        })
        .catch((err) => {
          this.onCopyError && this.onCopyError(err);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
/deep/ .wt2-dropdown {
  min-width: 150px;
}

/deep/ .wt2-text-field {
  margin-bottom: 7px;
  input.-mandatoryError::-webkit-input-placeholder {
    color: rgba(0, 0, 0, 0.5);
  }
  .-error-message {
    position: absolute;
    bottom: -20px;
  }
}
</style>
